/**
 * ★ WHAT THE APPLICATION'S LAST STEP ASKS FOR (design 25; design 24, T24 - the owner's E1 and E2).
 *
 * "Our standards and agreement" is the step before "Submit for review": one box per editorial
 * standard while the editorial standards have a version in force, and one, "I accept the author
 * agreement". They are the SUBMISSION documents - what holds a submit back until accepted (P12) -
 * and only those still owed are asked for. Ticking records nothing on its own: "Submit for review"
 * sends every ticked document in one `acceptAgreements` call, then submits
 * (../data/useApplicationActions.ts).
 *
 * WHERE THE LIST COMES FROM. `listAgreements`. When it cannot be read, the author agreement alone is
 * asked for, read from `getCurrentTerms` and accepted through `acceptTerms` as it always was - the
 * one document every server has - so a list that will not load never stops an application.
 *
 * THE THREE STATEMENTS are the editorial standards' own, word for word: the owner's reviewed text
 * of 12 October 2026 (`docs/design/27-launch-texts/03-editorial-standards.md`). The document lives on
 * moonx.app; the boxes say what it says, one consent each (E2: "three standards checkboxes plus one
 * for the author agreement").
 */
import type { AgreementList, TermsState } from '../contract';

/** The author agreement's document code (`terms_document`, seeded since 0003). */
export const AUTHOR_AGREEMENT_CODE = 'creator_program';

/** The editorial standards' document code (design 25, migration 0093). */
export const EDITORIAL_STANDARDS_CODE = 'editorial_standards';

/** The editorial standards, one box each, every one to be ticked: `application` catalogue keys. */
export const EDITORIAL_STANDARDS = [
  'agreement.standards.original',
  'agreement.standards.notAdvice',
  'agreement.standards.aiDisclosed',
] as const;

/** One document the last step asks for, and how its acceptance is sent. */
export interface SubmissionDocument {
  readonly documentCode: string;
  /** The server's title - drawn only for a document the cabinet does not know by its code. */
  readonly title: string;
  /** The version in force: what is accepted. */
  readonly versionLabel: string;
  readonly contentUrl: string;
  /** `agreements`: sent with `acceptAgreements`; `terms`: the fallback, sent with `acceptTerms`. */
  readonly via: 'agreements' | 'terms';
}

/**
 * The submission documents still owed, in the documents' order - or `undefined` while there is
 * nothing to tell from. `agreements` undefined means the list could not be read (the page waits for
 * its first answer), and the author agreement's own read answers instead.
 */
export function owedForSubmission(
  agreements: AgreementList | undefined,
  terms: TermsState | undefined,
): readonly SubmissionDocument[] | undefined {
  if (agreements !== undefined) {
    return agreements.items.flatMap((item) =>
      item.blocks === 'submission' && item.owed && item.live !== null
        ? [
            {
              documentCode: item.documentCode,
              title: item.title,
              versionLabel: item.live.versionLabel,
              contentUrl: item.live.contentUrl,
              via: 'agreements' as const,
            },
          ]
        : [],
    );
  }
  if (terms === undefined) {
    return undefined;
  }
  return terms.accepted
    ? []
    : [
        {
          documentCode: AUTHOR_AGREEMENT_CODE,
          title: 'Author agreement',
          versionLabel: terms.versionLabel,
          contentUrl: terms.contentUrl,
          via: 'terms',
        },
      ];
}
